import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import Rollbar from 'rollbar';
import { Subscription, firstValueFrom } from 'rxjs';

import { ROLLBAR_SERVICE } from 'src/app/app.module';
import {
    CONTACT_CATEGORIES, CONTACT_CATEGORY_DESCRIPTIONS, CONTACT_MAX_DESCRIPTION_LENGTH,
    CONTACT_MAX_EMAIL_LENGTH, CONTACT_MAX_NAME_LENGTH, CONTACT_MAX_SUBJECT_FIELD_LENGTH,
    ContactRequest, ContactSubjectFields, contactCategoryFromParam, getContactSubjectFields
} from 'src/app/models/contact.model';
import { TerpecaUser } from 'src/app/models/user.model';
import { AuthService } from 'src/app/services/auth.service';

import { SimpleDialogComponent } from '../simpledialog/simpledialog.component';

@Component({
  selector: 'app-contact',
  standalone: false,
  templateUrl: './contact.component.html',
  styleUrl: './contact.component.css'
})
export class ContactComponent implements OnInit, OnDestroy {
  private auth = inject(AuthService);
  private dialog = inject(MatDialog);
  private functions = inject(AngularFireFunctions);
  private rollbar = inject<Rollbar>(ROLLBAR_SERVICE);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  categories = CONTACT_CATEGORIES;
  categoryDescriptions = CONTACT_CATEGORY_DESCRIPTIONS;
  subjectFields: ContactSubjectFields = getContactSubjectFields('');
  formGroup: UntypedFormGroup;
  submitting = false;
  submitted = false;
  submittedAnonymous = false;
  private subscriptions: Subscription[] = [];

  ngOnInit() {
    this.formGroup = new UntypedFormGroup({
      anonymous: new UntypedFormControl(false),
      name: new UntypedFormControl('', [Validators.required, Validators.maxLength(CONTACT_MAX_NAME_LENGTH)]),
      email: new UntypedFormControl('', [
        Validators.required, Validators.email, Validators.maxLength(CONTACT_MAX_EMAIL_LENGTH)
      ]),
      category: new UntypedFormControl('', Validators.required),
      room: new UntypedFormControl('', Validators.maxLength(CONTACT_MAX_SUBJECT_FIELD_LENGTH)),
      company: new UntypedFormControl('', Validators.maxLength(CONTACT_MAX_SUBJECT_FIELD_LENGTH)),
      person: new UntypedFormControl('', Validators.maxLength(CONTACT_MAX_SUBJECT_FIELD_LENGTH)),
      description: new UntypedFormControl('', [
        Validators.required, Validators.maxLength(CONTACT_MAX_DESCRIPTION_LENGTH)
      ]),
    });
    if (this.auth.currentUser) {
      this.prefillContactFields(this.auth.currentUser);
    }
    this.subscriptions.push(
      this.formGroup.get('anonymous').valueChanges.subscribe((anonymous: boolean) => {
        this.setContactFieldsRequired(!anonymous);
        if (anonymous) {
          this.formGroup.patchValue({ name: '', email: '' });
        } else if (this.auth.currentUser) {
          this.prefillContactFields(this.auth.currentUser);
        }
      }),
      this.formGroup.get('category').valueChanges.subscribe((category: string) => {
        this.updateSubjectFields(category);
      }),
      this.auth.user.subscribe((user: TerpecaUser) => {
        if (user && !this.formGroup.value.anonymous) {
          this.prefillContactFields(user);
        }
      }),
      this.route.queryParamMap.subscribe(params => {
        if (this.submitted) {
          return;
        }
        const category = contactCategoryFromParam(params.get('category'));
        if (category && this.formGroup.value.category !== category) {
          this.formGroup.patchValue({ category });
        }
      }));
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => { subscription.unsubscribe(); });
  }

  async submit() {
    if (!this.formGroup.valid || this.submitting) {
      this.formGroup.markAllAsTouched();
      return;
    }
    this.submitting = true;
    const anonymous = Boolean(this.formGroup.value.anonymous);
    const payload: ContactRequest = {
      anonymous,
      category: this.formGroup.value.category,
      description: String(this.formGroup.value.description || '').trim(),
      source: 'portal',
    };
    const room = String(this.formGroup.value.room || '').trim();
    const company = String(this.formGroup.value.company || '').trim();
    const person = String(this.formGroup.value.person || '').trim();
    if (room) {
      payload.room = room;
    }
    if (company) {
      payload.company = company;
    }
    if (person) {
      payload.person = person;
    }
    if (!anonymous) {
      payload.name = String(this.formGroup.value.name || '').trim();
      payload.email = String(this.formGroup.value.email || '').trim();
    }
    try {
      await firstValueFrom(this.functions.httpsCallable<ContactRequest, { ok: boolean }>('submitContact')(payload));
      this.submittedAnonymous = anonymous;
      this.submitted = true;
    } catch (error) {
      this.rollbar.error('Error sending contact form', error);
      this.dialog.open(SimpleDialogComponent, {
        data: {
          title: 'Unable to send message',
          message: 'Sorry, we couldn\'t send your message. Please try again, or email admin@terpeca.com directly.',
          hideCancel: true,
        }
      });
    } finally {
      this.submitting = false;
    }
  }

  startNewContact() {
    this.submitted = false;
    this.submittedAnonymous = false;
    this.formGroup.reset({
      anonymous: false,
      name: '',
      email: '',
      category: '',
      room: '',
      company: '',
      person: '',
      description: '',
    });
    this.setContactFieldsRequired(true);
    this.updateSubjectFields('');
    if (this.auth.currentUser) {
      this.prefillContactFields(this.auth.currentUser);
    }
    this.formGroup.markAsPristine();
    this.formGroup.markAsUntouched();
    this.router.navigate(['/contact']);
  }

  private prefillContactFields(user: TerpecaUser) {
    const patch: { name?: string, email?: string } = {};
    if (!String(this.formGroup.value.name || '').trim()) {
      patch.name = user.realName || user.displayName || '';
    }
    if (!String(this.formGroup.value.email || '').trim()) {
      patch.email = user.appEmail || user.email || '';
    }
    if (patch.name || patch.email) {
      this.formGroup.patchValue(patch);
    }
  }

  private setContactFieldsRequired(required: boolean) {
    const name = this.formGroup.get('name');
    const email = this.formGroup.get('email');
    if (required) {
      name.setValidators([Validators.required, Validators.maxLength(CONTACT_MAX_NAME_LENGTH)]);
      email.setValidators([
        Validators.required, Validators.email, Validators.maxLength(CONTACT_MAX_EMAIL_LENGTH)
      ]);
    } else {
      name.clearValidators();
      email.clearValidators();
    }
    name.updateValueAndValidity();
    email.updateValueAndValidity();
  }

  private updateSubjectFields(category: string) {
    this.subjectFields = getContactSubjectFields(category);
    this.setSubjectFieldValidators('room', this.subjectFields.room);
    this.setSubjectFieldValidators('company', this.subjectFields.company);
    this.setSubjectFieldValidators('person', this.subjectFields.person);
    if (this.subjectFields.room === 'hidden') {
      this.formGroup.patchValue({ room: '' });
    }
    if (this.subjectFields.company === 'hidden') {
      this.formGroup.patchValue({ company: '' });
    }
    if (this.subjectFields.person === 'hidden') {
      this.formGroup.patchValue({ person: '' });
    }
    this.formGroup.updateValueAndValidity();
  }

  private setSubjectFieldValidators(fieldName: string, mode: ContactSubjectFields['room']) {
    const control = this.formGroup.get(fieldName);
    if (mode === 'required') {
      control.setValidators([Validators.required, Validators.maxLength(CONTACT_MAX_SUBJECT_FIELD_LENGTH)]);
    } else if (mode === 'optional') {
      control.setValidators([Validators.maxLength(CONTACT_MAX_SUBJECT_FIELD_LENGTH)]);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity();
  }
}
